/* eslint-disable react/react-in-jsx-scope */
import Financeurs from "@/assets/icones/financeurs.svg?react";
import { useAuth } from "@/auth/hooks/useAuth";
import ErrorBoundaryWrapper from "@/components/errors/ErrorBoundaryWrapper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  File,
  Folder,
  Tree,
} from "@/components/extension/tree-view-api-doc";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import {
  useCustomTheme,
  useFeedback,
  useLogoStore,
} from "@/dashboard/store/store";
import usePostData from "@/hooks/usePostData";
import { isFeatureEnabledForEmail } from "@/libs/featureAccess";
import { cn } from "@/libs/utils";
import {
  Award,
  FileStack,
  FolderGit2,
  FolderIcon,
  Handshake,
  LayoutDashboard,
  MessagesSquare,
  Paintbrush,
  Power,
  ScrollText,
  Settings,
  UserRoundCog,
} from "lucide-react";
import { FC, lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router-dom";

const TreeView = lazy(() => import("./TreeView"));
const TreeViewDocument = lazy(() => import("./TreeViewDocument"));
const TreeViewParameters = lazy(() => import("./TreeViewParameters"));

const NavigationTreeFallback = ({ label }: { label: string }) => (
  <div className="mb-4 flex items-center gap-2 py-1 text-[13px] text-muted-foreground">
    <FolderIcon className="size-5 opacity-70" />
    <span>{label}</span>
    <LoadingSpinner />
  </div>
);

type LeftbarProps = {
  handleToggleLeftbar: (val: boolean) => void;
  isOpen: boolean;
};
const Leftbar: FC<LeftbarProps> = ({ handleToggleLeftbar, isOpen }) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "leftbar",
  });
  const [loadingSaveFinancer, setLoadingSaveFinancer] = useState(false);
  const { isAuthenticated, logout, loading, user, updateUserState } = useAuth();
  const { setLogoUrl, logoUrl } = useLogoStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlCurrent = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1)
    : location.pathname;
  const parameters = searchParams.get("params");

  const { postData } = usePostData<any>();
  const { isVisible, setIsVisible } = useCustomTheme();
  const isFinancingAllowed = isFeatureEnabledForEmail(
    user?.email,
    import.meta.env.VITE_ALLOWED_FINANCING_USERS,
  );
  const isPureSubcontractor =
    !!user?.roles?.includes("ROLE_SUBCONTRACTOR") &&
    !user?.roles?.includes("ROLE_CARRIER");
  const isSynthesesAllowed = isFeatureEnabledForEmail(
    user?.email,
    import.meta.env.VITE_ALLOWED_SYNTHESES_USERS,
  );

  const [showCertificate, setShowCertificate] = useState(false);

  const { visibleFeedback, setVisibleFeedback } = useFeedback();

  const handleRemoveLogo = async () => {
    setLoadingSaveFinancer(true);
    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/theme`,
      data: {
        logo: "clear",
      },
    });
    if (res === null) {
      setLoadingSaveFinancer(false);
      return;
    }
    toast({
      title: "Votre profil a bien été modifié.",
      duration: 1000,
    });
    setLoadingSaveFinancer(false);
    if (user) {
      const next = {
        ...user,
        logo: null,
      };
      updateUserState(next);
      // Grâce à useAuth.ts, ceci va aussi faire :
      // localStorage.setItem("user", JSON.stringify(next));
    } else {
      // Ultime fallback si user est nul mais stocké en localStorage
      try {
        const raw = localStorage.getItem("user");
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.logo = null;
          localStorage.setItem("user", JSON.stringify(parsed));
        }
      } catch {}
    }
    setLogoUrl("");
  };
  void handleRemoveLogo;
  useEffect(() => {
    if (user && user?.Organisation?.CaseACocher?.length > 0) {
      if (user.Organisation.CaseACocher.includes("NCERFA")) {
        setShowCertificate(false);
      } else {
        setShowCertificate(true);
      }
    }
    if (user?.logo?.uri) {
      setLogoUrl(user.logo.uri);
    }
  }, [user, setLogoUrl]);

  return (
    <div
      id="sidebar"
      className={`sticky top-0 z-50 flex h-screen min-h-screen flex-col overflow-y-auto py-8 ${
        isOpen ? "pl-7 pr-2" : "flex flex-col items-center px-2"
      }   bg-cover bg-center`}
    >
      <div className="relative flex flex-col items-start gap-2 mb-7">
        <Link to="/dashboard">
          <div className={`max-w-32  overflow-hidden relative group`}>
            {loadingSaveFinancer && <LoadingSpinner />}
            {!loading &&
              (logoUrl ? (
                <img
                  src={import.meta.env.VITE_MEDIA_BASE + logoUrl}
                  alt="logo verdeo"
                  className=" w-full h-auto"
                />
              ) : (
                <img
                  src="/logo.svg"
                  alt="logo verdeo"
                  className=" w-24 h-auto"
                />
              ))}
          </div>
        </Link>
      </div>

      <ul>
        <LinkNav
          isOpen={isOpen}
          picto={<LayoutDashboard size={18} />}
          link="/"
          isActive={urlCurrent == "/dashboard"}
        >
          {t("Tableau de bord")}
        </LinkNav>
      </ul>

      {isOpen ? (
        <Suspense fallback={<NavigationTreeFallback label={t("Mes programmes")} />}>
          <ErrorBoundaryWrapper>
            <div className="ml-[-22px]">
              <TreeView />
            </div>
          </ErrorBoundaryWrapper>
        </Suspense>
      ) : (
        <button
          className="mb-4 w-7 flex justify-center"
          onClick={() => {
            handleToggleLeftbar(true);
          }}
        >
          <FolderIcon className="flex size-5 opacity-70" />{" "}
        </button>
      )}
      <ul>
        {user && !isPureSubcontractor && (
          <>
            {isOpen ? (
              <Tree
                initialSelectedId="finance-parent"
                initialExpendedItems={["finance-parent"]}
                className="relative ml-[-22px] mb-3"
                id="Mes financements"
                elements={[
                  {
                    id: "finance-parent",
                    name: "Mes financements",
                    isSelectable: false,
                  },
                ]}
              >
                <Folder
                  element="Mes financements"
                  value="finance-parent"
                  visibleChevron
                >
                  <div className="flex flex-col gap-1">
                    <File
                      value="file-financeurs"
                      className="ml-3 py-1"
                      fileIcon={
                        <Financeurs
                          fill="#878787"
                          className="relative bottom-0.5 size-5"
                        />
                      }
                      title={
                        isFinancingAllowed ? t("Mes financeurs") : "Bientôt disponible"
                      }
                      isSelectable={isFinancingAllowed}
                      isSelect={
                        urlCurrent == "/dashboard/mes-financeurs" ||
                        urlCurrent == "/dashboard/ajout-financeur"
                      }
                      url="/dashboard/mes-financeurs"
                    >
                      <span className="w-full flex-1 truncate text-muted-foreground">
                        {t("Mes financeurs")}
                      </span>
                    </File>
                    <File
                      value="file-certificats"
                      className="ml-3 py-1"
                      fileIcon={<Award size={18} className="text-muted-foreground" />}
                      title="Mes reçus fiscaux"
                      isSelectable={showCertificate}
                      isSelect={urlCurrent == "/dashboard/certificats"}
                      url="/dashboard/certificats"
                    >
                      <span className="w-full flex-1 truncate text-muted-foreground">
                        Mes reçus fiscaux
                      </span>
                    </File>
                  </div>
                </Folder>
              </Tree>
            ) : (
              <button
                className="mb-4 flex w-7 justify-center"
                onClick={() => handleToggleLeftbar(true)}
                title="Mes financements"
              >
                <FolderIcon className="flex size-5 opacity-70" />
              </button>
            )}
            {isSynthesesAllowed && (
              <LinkNav
                isOpen={isOpen}
                picto={<ScrollText size={18} />}
                link="/syntheses"
                title="Suivi de financements"
                className=""
                isActive={urlCurrent.startsWith("/dashboard/syntheses")}
              >
                Suivi de financements
              </LinkNav>
            )}
            <LinkNav
              isOpen={isOpen}
              picto={<Handshake size={18} />}
              link="/subcontractors"
              title={t("Mes intervenants")}
              className=""
              isActive={urlCurrent == "/dashboard/subcontractors"}
            >
              {t("Mes intervenants")}
            </LinkNav>
            {user &&
              user?.roles?.includes("ROLE_CARRIER") &&
              user?.coworker !== true && (
                <LinkNav
                  isOpen={isOpen}
                  picto={<UserRoundCog size={18} />}
                  link="/user-management"
                  title={"Mes collaborateurs"}
                  className=""
                  isActive={urlCurrent == "/dashboard/user-management"}
                >
                  {"Mes collaborateurs"}
                </LinkNav>
              )}
          </>
        )}
        {isOpen ? (
          <Suspense fallback={<NavigationTreeFallback label="Mes documents" />}>
            <ErrorBoundaryWrapper>
              <div className="relative ml-[-22px]">
                <TreeViewDocument />
              </div>
            </ErrorBoundaryWrapper>
          </Suspense>
        ) : (
          <button
            className="mb-4"
            onClick={() => {
              handleToggleLeftbar(true);
            }}
          >
            <ScrollText className="flex size-5 opacity-70" />{" "}
          </button>
        )}
        {user && !isPureSubcontractor && (
          <>
            <LinkNav
              isOpen={isOpen}
              picto={<FileStack size={18} />}
              link="/parameters?params=2"
              title="Mes formulaires"
              className=""
              isActive={urlCurrent == "/dashboard/parameters" && parameters === "2"}
            >
              Mes formulaires
            </LinkNav>
            <LinkNav
              isOpen={isOpen}
              picto={<FolderGit2 size={18} />}
              link="/call-for-projects"
              title={t("Mes Appels à Projets")}
              className=""
              isActive={urlCurrent == "/dashboard/call-for-projects"}
            >
              {t("Mes Appels à Projets")}
            </LinkNav>

            {/* <LinkNav isOpen={isOpen} picto={<User2 size={18} />} link="/">
            Profil
          </LinkNav> */}
            {/* <AlertDialog
            open={openNotifications}
            onOpenChange={setOpenNotifications}
          >
            <AlertDialogTrigger asChild>
              <li className="mb-4" title={""}>
                <Link
                id="notification"
                  onClick={e => {
                    e.preventDefault();

                    setCountNotification(0);

                  postData({
                    url: `${import.meta.env.VITE_IP_API}/notification/read-all`,
                    data: {
                      ids: notifications.filter(n => n.status === 0).map(n => n.id)
                    },
                  });

                  setOpenNotifications(true);
                }}
                to={`/dashboard`}
                className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 text-muted-foreground  hover:text-[#1C5114] ${
                  !isOpen ? "mb-3  justify-center" : "mb-3"
                }`}
              >
                <Bell size={18} /> {isOpen && <>{t("Notifications")}{countNotification > 0 && !openNotifications && <Badge>{countNotification}</Badge>}</>}
              </Link>
            </li>
          </AlertDialogTrigger>
          <AlertDialogContent>
          {loadingReadNotification && <div className="fixed left-0 top-0 z-[9999999999] flex size-full items-center justify-center bg-[#00000080]">
            <div className="flex gap-2 text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                color="#fff"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-spin"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              {t("Chargement")}...  
            </div>  
          </div>}
            <AlertDialogHeader>
              <AlertDialogTitle>
              Notifications
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="mt-3 max-h-[50vh] overflow-y-auto">
              <dl className="max-w-md text-gray-900 divide-y divide-gray-200 dark:text-white dark:divide-gray-700 cursor-pointer">
                  {notifications.length === 0 && <p className="m-0">Aucune notification.</p>}
                  {notifications.map((notification) => <div className={`flex flex-col py-3 px-3 ${notification.status === 0 ? 'bg-slate-100' : ''}`} onClick={async () => {
                    const ids = notification.id.split('_');
                    const idProject = ids[0];
                    setLoadingReadNotification(true);
                    await postData({
                      url: `${import.meta.env.VITE_IP_API}/notification/read/${
                        notification.id
                      }`,
                      data: {},
                    });
                    window.location.href = `/dashboard/project/${idProject}?tab=suivi-perf`
                  }}>
                    <dt className="mb-1 flex gap-4 text-sm text-gray-500" title={notification.message}>
                      <div className="size-[100px] overflow-hidden rounded-sm">
                        <img className="size-full object-cover" src={import.meta.env.VITE_MEDIA_BASE + notification.thumb} alt={notification.message} />
                      </div>
                    </dt>
                  </div>
                ))}
              </dl>
            </div>
            <AlertDialogFooter className="flex sm:justify-between items-center">
              <div className="flex items-center gap-2 cursor-pointer text-[14px] text-primary" onClick={() => {
                document.location.href = "/dashboard/preference/notification";
              }}><SettingsIcon /> Mes préférences</div>
              <AlertDialogCancel>Fermer</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog> */}
            <LinkNav
              isOpen={isOpen}
              picto={<MessagesSquare size={18} />}
              link="/message"
              title="Bientôt disponible"
              className="pointer-events-none opacity-45"
              isActive={urlCurrent == "/dashboard/message"}
            >
              {t("Messages")}
            </LinkNav>
            {isPureSubcontractor ? (
              <LinkNav
                isOpen={isOpen}
                picto={<Settings size={18} />}
                link="/parameters"
                title={"Paramètres"}
                className=""
                isActive={urlCurrent == "/dashboard/parameters"}
              >
                {"Paramètres"}
              </LinkNav>
            ) : isOpen ? (
              <Suspense fallback={<LoadingSpinner />}>
                <ErrorBoundaryWrapper>
                  <div className="relative ml-[-22px]">
                    <TreeViewParameters isOpen={isOpen} />
                  </div>
                </ErrorBoundaryWrapper>
              </Suspense>
            ) : (
              <button
                className="mb-4"
                onClick={() => {
                  handleToggleLeftbar(true);
                }}
                title="Paramètres"
              >
                <Settings className="flex size-5 opacity-70" />
              </button>
            )}

            <li>
              <div
                className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 cursor-pointer text-muted-foreground hover:text-primary  ${
                  !isOpen ? "mb-3 w-7 justify-center" : "mb-3 "
                }
            `}
                onClick={() => setIsVisible(!isVisible)}
              >
                <Paintbrush size={18} /> {isOpen && "Personnalisation"}
              </div>
            </li>
            <li>
              <div
                className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 cursor-pointer text-muted-foreground hover:text-primary  ${
                  !isOpen ? "mb-3 w-7 justify-center" : "mb-3 "
                }
            `}
                onClick={() => setVisibleFeedback(!visibleFeedback)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-megaphone-icon lucide-megaphone"
                >
                  <path d="M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
                  <path d="M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14" />
                  <path d="M8 6v8" />
                </svg>{" "}
                {t("Signaler un problème")}
              </div>
            </li>
          </>
        )}
      </ul>

      <ul className="mt-auto">
        {/* <LinkNav
          isOpen={isOpen}
          picto={<Settings size={20} />}
          link="/parametre"
          className="pointer-events-none mb-0 opacity-45"
          isActive={urlCurrent == '/dashboard/parametre'}
        >
          {t("Paramètres")}
        </LinkNav> */}
        <div
          className={`mt-auto flex flex-wrap ${
            isOpen ? "justify-between" : "justify-center"
          } items-center transition-all`}
        >
          {!isAuthenticated && !loading ? (
            <Link
              to="/login"
              className="rounded-[4rem] border-2 border-foreground bg-background px-4 py-2 text-muted-foreground transition-all hover:bg-foreground hover:text-background"
            >
              {t("Connexion")}
            </Link>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <div className="flex cursor-pointer items-center gap-2 text-muted-foreground transition-all hover:text-primary group">
                  <Power
                    size={18}
                    className="text-muted-foreground group-hover:text-primary transition-all"
                  />
                  {isOpen && t("Déconnexion")}
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("Êtes-vous sûr de vouloir vous déconnecter ?")}
                  </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Annuler")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => logout(window.location.href)}
                  >
                    {t("Se déconnecter")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </ul>
    </div>
  );
};

const LinkNav = ({
  isActive,
  isOpen,
  link,
  title,
  picto,
  children,
  className,
}: {
  isActive?: boolean;
  isOpen: boolean;
  link: string;
  title?: string;
  picto?: ReactNode;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <li
      className={cn("mb-4", !isOpen ? "justify-center flex " : "", className)}
      title={title}
    >
      <Link
        to={`/dashboard${link}`}
        className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 hover:text-primary group ${
          isActive ? "text-primary font-medium" : "text-muted-foreground"
        } ${!isOpen ? "" : "mb-3"}
        `}
        title={children?.toString()}
      >
        {picto}{" "}
        {isOpen && (
          <span className="truncate" style={{ width: "calc(100% - 30px)" }}>
            {children}
          </span>
        )}
      </Link>
    </li>
  );
};
export default Leftbar;
